export const layoutRecordId = (row) =>
  row?._id ?? row?.id ?? row?.layoutId ?? "";

export const findClubById = (clubList, clubId) => {
  if (!Array.isArray(clubList) || !clubId) return null;
  const needle = String(clubId).trim();
  return (
    clubList.find(
      (c) => String(c?._id ?? c?.id ?? "").trim() === needle
    ) ?? null
  );
};

export const clubDisplayName = (club, fallback = "") =>
  String(club?.name ?? club?.slug ?? fallback).trim() || fallback;

export const normalizeLayoutsList = (res) => {
  const raw = res?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.layouts)) return raw.layouts;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

/** Single layout from GET .../layouts/:layoutId (or embedded in list item). */
export const normalizeLayoutDetail = (res) => {
  const raw = res?.data ?? res;
  if (!raw || typeof raw !== "object") return null;
  if (raw.layout && typeof raw.layout === "object") return raw.layout;
  if (raw._id != null || raw.id != null || raw.layoutId != null) return raw;
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return null;
};

export const normalizeBookingsList = (res) => {
  const raw = res?.data ?? res;
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (Array.isArray(raw?.bookings)) list = raw.bookings;
  else if (Array.isArray(raw?.data)) list = raw.data;
  return sortBookingsByCreatedDesc(list);
};

export const pickBookingCreatedRaw = (b) =>
  b?.createdAt ?? b?.created_at ?? b?.created ?? "";

export const pickBookingCreatedTime = (b) => {
  const d = new Date(pickBookingCreatedRaw(b));
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

/** Newest booking first. */
export const sortBookingsByCreatedDesc = (rows) =>
  [...(rows || [])].sort(
    (a, b) => pickBookingCreatedTime(b) - pickBookingCreatedTime(a)
  );

/** Backend ClubTableBooking fields: bookingref, servicedate, slotkey, userid (populated). */
export const pickBookingRef = (b) =>
  b?.bookingref ??
  b?.bookingRef ??
  b?.reference ??
  b?.ref ??
  b?.bookingReference ??
  b?._id ??
  b?.id ??
  "—";

export const pickBookingUser = (b) => {
  // API enrichment uses `booker` (instead of `userid`) on most endpoints.
  const u =
    b?.booker ??
    b?.userid ??
    b?.user ??
    b?.customer ??
    b?.guest ??
    null;
  if (u && typeof u === "object") return u;
  return {};
};

export const pickBookingGuestName = (b) => {
  const u = pickBookingUser(b);
  const joined = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return (
    joined ||
    u.fullname ||
    u.fullName ||
    u.name ||
    b?.contactName ||
    b?.guestName ||
    "—"
  );
};

export const pickBookingGuestEmail = (b) => {
  const u = pickBookingUser(b);
  return u.email || b?.contactEmail || b?.guestEmail || "";
};

export const pickBookingGuestPhone = (b) => {
  const u = pickBookingUser(b);
  return u.phone || u.phoneNumber || u.mobile || b?.contactPhone || "";
};

export const pickBookingAmount = (b) =>
  b?.amount ??
  b?.depositAmount ??
  b?.payNowAmount ??
  b?.totalAmount ??
  b?.total ??
  b?.minimumSpend ??
  b?.price ??
  null;

export const pickBookingCurrency = (b) =>
  b?.currency ?? b?.currencyCode ?? "";

export const pickBookingPartySize = (b) =>
  b?.partySize ?? b?.party_size ?? b?.guests ?? b?.headcount ?? "—";

export const pickBookingDate = (b) => {
  const raw =
    b?.servicedate ??
    b?.serviceDate ??
    b?.date ??
    b?.bookingDate ??
    b?.eventDate ??
    b?.slotDate ??
    "";
  const s = String(raw).trim();
  return s || "—";
};

export const pickBookingSlot = (b) => {
  const label =
    b?.slotLabel ??
    b?.slot?.label ??
    b?.slot_label ??
    "";
  if (label && String(label).trim()) return String(label).trim();
  const key =
    b?.slotkey ??
    b?.slotKey ??
    b?.slot?.key ??
    b?.timeSlot ??
    "";
  const k = String(key).trim();
  if (!k) return "—";
  return k.replace(/_/g, " ");
};

/** layoutId → slotKey → slot { label, startTime, endTime } */
export const buildLayoutSlotLookup = (layouts) => {
  const byLayout = {};
  for (const layout of layouts || []) {
    const layoutId = String(layoutRecordId(layout)).trim();
    if (!layoutId) continue;
    const slots = {};
    for (const slot of layout?.slots || []) {
      const key = String(slot?.key ?? "").trim();
      if (key) slots[key] = slot;
    }
    byLayout[layoutId] = slots;
  }
  return byLayout;
};

const pickBookingLayoutId = (b) =>
  String(
    b?.layoutid ??
      b?.layoutId ??
      b?.layout?._id ??
      b?.layout?.id ??
      ""
  ).trim();

const pickBookingSlotKey = (b) =>
  String(
    b?.slotkey ?? b?.slotKey ?? b?.slot?.key ?? b?.timeSlot ?? ""
  ).trim();

const resolveLayoutSlotDef = (slotLookup, layoutId, slotKey) => {
  if (!layoutId || !slotKey) return null;
  const slots = slotLookup?.[layoutId];
  if (!slots) return null;
  if (slots[slotKey]) return slots[slotKey];
  const lower = slotKey.toLowerCase();
  const matchKey = Object.keys(slots).find((k) => k.toLowerCase() === lower);
  return matchKey ? slots[matchKey] : null;
};

const formatSlotTimeRange = (slotDef) => {
  const start = String(slotDef?.startTime ?? "").trim();
  const end = String(slotDef?.endTime ?? "").trim();
  if (start && end) return `${start} – ${end}`;
  return start || end || "";
};

/** Human-readable slot: layout label + time, not internal slot_* keys. */
export const formatBookingSlot = (b, slotLookup = {}) => {
  const inlineLabel = String(
    b?.slotLabel ?? b?.slot?.label ?? b?.slot_label ?? ""
  ).trim();
  if (inlineLabel) return inlineLabel;

  const layoutId = pickBookingLayoutId(b);
  const slotKey = pickBookingSlotKey(b);
  const slotDef = resolveLayoutSlotDef(slotLookup, layoutId, slotKey);

  if (slotDef) {
    const label = String(slotDef.label ?? "").trim();
    const timeRange = formatSlotTimeRange(slotDef);
    if (label && timeRange) return `${label} (${timeRange})`;
    if (label) return label;
    if (timeRange) return timeRange;
  }

  if (!slotKey) return "—";
  if (/^slot_\d+_[a-z0-9]+$/i.test(slotKey)) return "—";
  return slotKey.replace(/_/g, " ");
};

export const formatBookingCreated = (b) => {
  const raw = pickBookingCreatedRaw(b);
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString();
};

export const countAreas = (layout) =>
  Array.isArray(layout?.areas) ? layout.areas.length : 0;

export const countSlots = (layout) =>
  Array.isArray(layout?.slots) ? layout.slots.length : 0;

export const DEFAULT_TIMEZONE = "Asia/Kolkata";

export const DEFAULT_MANUAL_AVAILABILITY = "AUTO";

/** Channel sold-out flags: missing/undefined → false for layout payloads. */
export const toSoldOutFlag = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  return false;
};

/**
 * Explicit event override: true/false when the key is present, else undefined
 * so the layout value is used.
 */
export const pickOptionalSoldOutFlag = (row, camelKey, snakeKey) => {
  if (!row || typeof row !== "object") return undefined;
  const keys = [camelKey, snakeKey].filter(Boolean);
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) continue;
    const raw = row[key];
    if (raw === undefined || raw === null || raw === "") continue;
    return toSoldOutFlag(raw);
  }
  return undefined;
};

export const manualAvailabilityButtonLabel = (value, t) => {
  const k = String(value ?? "").toUpperCase();
  if (k === "SOLD_OUT") return "SOLD OUT";
  if (k === "AUTO") return t?.("TABLE_BOOKING_LEGEND_AVAILABLE") ?? "Available";
  return k;
};

export const emptySlot = () => ({
  key: `slot_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  startTime: "20:00",
  endTime: "23:00",
});

/** Nested bookable leaf under a zone (optional). Empty string = inherit from zone where applicable. */
export const emptyTable = () => ({
  label: "",
  code: "",
  hotspot: { x: 0.5, y: 0.5, radius: 0.02 },
  priceFromPerPerson: "",
  currency: "",
  priceDisplayLabel: "",
  totalTableSlots: 1,
  minPartySize: "",
  maxPartySize: "",
  description: "",
  mealSummary: "",
  panoramaUrl: "",
  manualAvailability: DEFAULT_MANUAL_AVAILABILITY,
  onlineSoldOut: false,
  offlineSoldOut: false,
});

export const emptyArea = () => ({
  label: "",
  code: "",
  hotspot: { x: 0.5, y: 0.5, radius: 0.04 },
  priceFromPerPerson: "",
  currency: "INR",
  priceDisplayLabel: "",
  totalTableSlots: 1,
  minPartySize: 1,
  maxPartySize: 10,
  description: "",
  mealSummary: "",
  panoramaUrl: "",
  manualAvailability: DEFAULT_MANUAL_AVAILABILITY,
  onlineSoldOut: false,
  offlineSoldOut: false,
  tables: [],
  focusBounds: null,
  outlinePolygon: null,
});

export const emptyLayoutForm = () => ({
  viewkey: "",
  title: "",
  sceneimageurl: "",
  sortorder: 0,
  timezone: DEFAULT_TIMEZONE,
  slots: [emptySlot()],
  areas: [emptyArea()],
});

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isValidSlotTime = (s) => typeof s === "string" && HH_MM.test(s);

/** Parse per-person price without rounding down or subtracting. */
export const parsePriceFromPerson = (value) => {
  if (value === "" || value == null) return 0;
  const cleaned = String(value).replace(/,/g, "").trim();
  if (!cleaned) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

/** Prevent scroll wheel from changing focused number inputs (common −1 bug). */
export const blockWheelChangeOnNumberInput = (e) => {
  e.preventDefault();
};

export const normalizeHotspot = (h) => {
  const x = Number(h?.x);
  const y = Number(h?.y);
  const radius = Number(h?.radius ?? 0.04);
  return {
    x: Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0.5,
    y: Number.isFinite(y) ? Math.min(1, Math.max(0, y)) : 0.5,
    radius: Number.isFinite(radius) ? Math.min(1, Math.max(0.005, radius)) : 0.04,
  };
};

const norm01 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : null;
};

export const normalizeFocusBounds = (fb) => {
  if (!fb || typeof fb !== "object") return null;
  let xMin = norm01(fb.xMin);
  let yMin = norm01(fb.yMin);
  let xMax = norm01(fb.xMax);
  let yMax = norm01(fb.yMax);
  if (xMin == null || yMin == null || xMax == null || yMax == null) return null;
  if (xMin > xMax) [xMin, xMax] = [xMax, xMin];
  if (yMin > yMax) [yMin, yMax] = [yMax, yMin];
  return { xMin, yMin, xMax, yMax };
};

const normalizeOutlinePoint = (p) => {
  if (Array.isArray(p) && p.length >= 2) {
    const x = norm01(p[0]);
    const y = norm01(p[1]);
    if (x == null || y == null) return null;
    return { x, y };
  }
  const x = norm01(p?.x ?? p?.X);
  const y = norm01(p?.y ?? p?.Y);
  if (x == null || y == null) return null;
  return { x, y };
};

/** Closed polygon for area outline (≥3 points, normalized 0–1). */
export const normalizeOutlinePolygon = (pts) => {
  if (!Array.isArray(pts)) return null;
  const out = pts.map(normalizeOutlinePoint).filter(Boolean);
  return out.length >= 3 ? out : null;
};

/** Read custom outline from API (never synthesize a rectangle from focusBounds). */
export const pickOutlinePolygonFromApi = (area) => {
  if (!area || typeof area !== "object") return null;
  const candidates = [
    area.outlinePolygon,
    area.areaOutline,
    area.outline,
    area.polygon,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const poly = normalizeOutlinePolygon(candidates[i]);
    if (poly) return poly;
  }
  return null;
};

/** Default 4-corner rectangle around zone hotspot for first-time editing. */
export const defaultOutlineAroundHotspot = (hotspot, pad = 0.1) => {
  const h = normalizeHotspot(hotspot);
  const px = Math.min(pad, h.x, 1 - h.x, 0.2);
  const py = Math.min(pad, h.y, 1 - h.y, 0.2);
  return normalizeOutlinePolygon([
    { x: h.x - px, y: h.y - py },
    { x: h.x + px, y: h.y - py },
    { x: h.x + px, y: h.y + py },
    { x: h.x - px, y: h.y + py },
  ]);
};

export const polygonFromFocusBounds = (fb) => {
  const b = normalizeFocusBounds(fb);
  if (!b) return null;
  return normalizeOutlinePolygon([
    { x: b.xMin, y: b.yMin },
    { x: b.xMax, y: b.yMin },
    { x: b.xMax, y: b.yMax },
    { x: b.xMin, y: b.yMax },
  ]);
};

export const focusBoundsFromPolygon = (polygon) => {
  const poly = normalizeOutlinePolygon(polygon);
  if (!poly) return null;
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  return normalizeFocusBounds({
    xMin: Math.min(...xs),
    yMin: Math.min(...ys),
    xMax: Math.max(...xs),
    yMax: Math.max(...ys),
  });
};

/** In-progress or finished outline points (1+ normalized vertices). */
export const normalizeOutlineDraft = (pts) => {
  if (!Array.isArray(pts)) return null;
  const out = pts.map(normalizeOutlinePoint).filter(Boolean);
  return out.length ? out : null;
};

/** Polygon for map rendering — only the saved custom shape, not focusBounds bbox. */
export const getAreaOutlineForDisplay = (area) => {
  const draft = normalizeOutlineDraft(area?.outlinePolygon);
  if (draft) return draft;
  return pickOutlinePolygonFromApi(area);
};

export const syncAreaOutlineAndBounds = (area, polygon) => {
  const draft = normalizeOutlineDraft(polygon);
  if (!draft) {
    return { outlinePolygon: null, focusBounds: null };
  }
  const poly =
    draft.length >= 3 ? normalizeOutlinePolygon(draft) || draft : draft;
  const fb =
    poly && poly.length >= 3 ? focusBoundsFromPolygon(poly) : null;
  return {
    outlinePolygon: poly,
    focusBounds: fb,
  };
};

const outlineCacheKey = (clubId, layoutId) =>
  `table-booking-outlines:${clubId}:${layoutId || "new"}`;

/** Keep custom shapes locally when API omits outlinePolygon (stripUnknown). */
export const cacheLayoutAreaOutlines = (clubId, layoutId, areas) => {
  if (!clubId || !Array.isArray(areas)) return;
  try {
    const rows = areas
      .map((a) => {
        const poly = normalizeOutlinePolygon(a?.outlinePolygon);
        if (!poly) return null;
        return {
          _id: a._id,
          code: a.code,
          label: a.label,
          outlinePolygon: poly,
        };
      })
      .filter(Boolean);
    if (!rows.length) {
      sessionStorage.removeItem(outlineCacheKey(clubId, layoutId));
      return;
    }
    sessionStorage.setItem(
      outlineCacheKey(clubId, layoutId),
      JSON.stringify(rows)
    );
  } catch {
    /* ignore quota / private mode */
  }
};

export const mergeCachedOutlinesIntoLayout = (layout, clubId, layoutId) => {
  if (!layout || !clubId) return layout;
  try {
    const raw = sessionStorage.getItem(outlineCacheKey(clubId, layoutId));
    if (!raw) return layout;
    const cached = JSON.parse(raw);
    if (!Array.isArray(cached) || !cached.length) return layout;
    const areas = (layout.areas || []).map((a) => {
      if (pickOutlinePolygonFromApi(a)) return a;
      const hit = cached.find(
        (c) =>
          (c._id && a._id && String(c._id) === String(a._id)) ||
          (c.code && a.code && String(c.code) === String(a.code)) ||
          (c.label && a.label && String(c.label).trim() === String(a.label).trim())
      );
      const poly = normalizeOutlinePolygon(hit?.outlinePolygon);
      if (!poly) return a;
      const fb = focusBoundsFromPolygon(poly);
      return {
        ...a,
        outlinePolygon: poly,
        ...(fb ? { focusBounds: fb } : {}),
      };
    });
    return { ...layout, areas };
  } catch {
    return layout;
  }
};

const mapTableFromApi = (t) => ({
  _id: t?._id != null ? String(t._id) : undefined,
  label: String(t?.label ?? ""),
  code: t?.code != null ? String(t.code) : "",
  hotspot: normalizeHotspot({
    ...t?.hotspot,
    radius: t?.hotspot?.radius ?? 0.02,
  }),
  priceFromPerPerson:
    t?.priceFromPerPerson != null && t?.priceFromPerPerson !== ""
      ? parsePriceFromPerson(t.priceFromPerPerson)
      : "",
  currency: t?.currency != null ? String(t.currency) : "",
  priceDisplayLabel: String(t?.priceDisplayLabel ?? ""),
  totalTableSlots:
    t?.totalTableSlots != null && t?.totalTableSlots !== ""
      ? Number(t.totalTableSlots) || 1
      : 1,
  minPartySize:
    t?.minPartySize != null && t?.minPartySize !== ""
      ? Number(t.minPartySize)
      : "",
  maxPartySize:
    t?.maxPartySize != null && t?.maxPartySize !== ""
      ? Number(t.maxPartySize)
      : "",
  description: String(t?.description ?? ""),
  mealSummary: String(t?.mealSummary ?? ""),
  panoramaUrl: String(t?.panoramaUrl ?? ""),
  manualAvailability: t?.manualAvailability
    ? String(t.manualAvailability).toUpperCase()
    : "",
  onlineSoldOut: toSoldOutFlag(t?.onlineSoldOut ?? t?.online_sold_out),
  offlineSoldOut: toSoldOutFlag(t?.offlineSoldOut ?? t?.offline_sold_out),
});

/** Prefer `tables`; if missing/empty, accept legacy aliases from API. */
export const pickAreaTablesFromApi = (a) => {
  const tables = a?.tables;
  const partyTables = a?.partyTables;
  const nestedTables = a?.nestedTables;
  if (Array.isArray(tables) && tables.length > 0) return tables;
  if (Array.isArray(partyTables) && partyTables.length > 0) return partyTables;
  if (Array.isArray(nestedTables) && nestedTables.length > 0)
    return nestedTables;
  return Array.isArray(tables) ? tables : [];
};

export const layoutToFormState = (layout) => ({
  viewkey: String(layout?.viewkey ?? "").toLowerCase(),
  title: String(layout?.title ?? ""),
  sceneimageurl: String(layout?.sceneimageurl ?? layout?.sceneImageUrl ?? ""),
  sortorder: Number(layout?.sortorder ?? layout?.sortOrder ?? 0) || 0,
  timezone: layout?.timezone || DEFAULT_TIMEZONE,
  slots:
    Array.isArray(layout?.slots) && layout.slots.length
      ? layout.slots.map((s) => ({
          key: String(s?.key ?? `slot_${Date.now()}`),
          label: String(s?.label ?? ""),
          startTime: String(s?.startTime ?? s?.start ?? "20:00"),
          endTime: String(s?.endTime ?? s?.end ?? "23:00"),
        }))
      : [emptySlot()],
  areas:
    Array.isArray(layout?.areas) && layout.areas.length
      ? layout.areas.map((a) => ({
          _id: a?._id != null ? String(a._id) : undefined,
          label: String(a?.label ?? ""),
          code: a?.code != null ? String(a.code) : "",
          hotspot: normalizeHotspot(a?.hotspot),
          priceFromPerPerson: parsePriceFromPerson(a?.priceFromPerPerson),
          currency: String(a?.currency ?? "INR"),
          priceDisplayLabel: String(a?.priceDisplayLabel ?? ""),
          totalTableSlots: Number(a?.totalTableSlots ?? 1) || 1,
          minPartySize: Number(a?.minPartySize ?? 1) || 1,
          maxPartySize: Number(a?.maxPartySize ?? 10) || 10,
          description: String(a?.description ?? ""),
          mealSummary: String(a?.mealSummary ?? ""),
          panoramaUrl: String(a?.panoramaUrl ?? ""),
          manualAvailability: String(
            a?.manualAvailability ?? DEFAULT_MANUAL_AVAILABILITY
          ).toUpperCase(),
          onlineSoldOut: toSoldOutFlag(
            a?.onlineSoldOut ?? a?.online_sold_out
          ),
          offlineSoldOut: toSoldOutFlag(
            a?.offlineSoldOut ?? a?.offline_sold_out
          ),
          tables: pickAreaTablesFromApi(a).map(mapTableFromApi),
          focusBounds: normalizeFocusBounds(a?.focusBounds),
          outlinePolygon: pickOutlinePolygonFromApi(a),
        }))
      : [emptyArea()],
});

const tableToPayload = (t) => {
  const row = {
    ...(t._id ? { _id: t._id } : {}),
    label: String(t.label ?? "").trim(),
    hotspot: normalizeHotspot({
      ...t.hotspot,
      radius: t?.hotspot?.radius ?? 0.02,
    }),
  };
  const code = String(t.code ?? "").trim();
  if (code) row.code = code;

  const p = t.priceFromPerPerson;
  if (p !== "" && p != null && String(p).trim() !== "") {
    row.priceFromPerPerson = parsePriceFromPerson(p);
  }

  const cur = String(t.currency ?? "").trim();
  if (cur) row.currency = cur;

  const pdl = String(t.priceDisplayLabel ?? "").trim();
  if (pdl) row.priceDisplayLabel = pdl;

  const slots = Number(t.totalTableSlots);
  if (Number.isFinite(slots) && slots >= 1) row.totalTableSlots = slots;
  else row.totalTableSlots = 1;

  const minP = t.minPartySize;
  if (minP !== "" && minP != null && Number.isFinite(Number(minP))) {
    row.minPartySize = Number(minP);
  }

  const maxP = t.maxPartySize;
  if (maxP !== "" && maxP != null && Number.isFinite(Number(maxP))) {
    row.maxPartySize = Number(maxP);
  }

  const desc = String(t.description ?? "").trim();
  if (desc) row.description = desc;

  const meal = String(t.mealSummary ?? "").trim();
  if (meal) row.mealSummary = meal;

  const pan = String(t.panoramaUrl ?? "").trim();
  if (pan) row.panoramaUrl = pan;

  const ma = String(t.manualAvailability ?? "").trim().toUpperCase();
  if (ma && ma !== "AUTO") row.manualAvailability = ma;

  row.onlineSoldOut = toSoldOutFlag(t.onlineSoldOut);
  row.offlineSoldOut = toSoldOutFlag(t.offlineSoldOut);

  return row;
};

export const formStateToPayload = (form) => ({
  viewkey: form.viewkey.trim().toLowerCase(),
  title: form.title.trim(),
  sceneimageurl: form.sceneimageurl.trim(),
  sortorder: Number(form.sortorder) || 0,
  timezone: form.timezone || DEFAULT_TIMEZONE,
  slots: form.slots.map((s) => ({
    key: s.key,
    label: s.label ?? "",
    startTime: s.startTime,
    endTime: s.endTime,
  })),
  areas: form.areas.map((a) => {
    const poly = normalizeOutlinePolygon(a.outlinePolygon);
    const fb =
      (poly && focusBoundsFromPolygon(poly)) ||
      normalizeFocusBounds(a.focusBounds);
    const zone = {
      ...(a._id ? { _id: a._id } : {}),
      label: a.label,
      code: a.code || undefined,
      hotspot: normalizeHotspot(a.hotspot),
      priceFromPerPerson: parsePriceFromPerson(a.priceFromPerPerson),
      currency: a.currency || "INR",
      priceDisplayLabel: a.priceDisplayLabel || undefined,
      totalTableSlots: Number(a.totalTableSlots) || 1,
      minPartySize: Number(a.minPartySize) || 1,
      maxPartySize: Number(a.maxPartySize) || 10,
      description: a.description || undefined,
      mealSummary: a.mealSummary || undefined,
      panoramaUrl: a.panoramaUrl || undefined,
      manualAvailability: a.manualAvailability || DEFAULT_MANUAL_AVAILABILITY,
      onlineSoldOut: toSoldOutFlag(a.onlineSoldOut),
      offlineSoldOut: toSoldOutFlag(a.offlineSoldOut),
    };
    if (fb) zone.focusBounds = fb;
    if (poly) {
      zone.outlinePolygon = poly;
      zone.areaOutline = poly;
    }

    const tables = Array.isArray(a.tables) ? a.tables : [];
    zone.tables = tables.map((t) => tableToPayload(t));

    return zone;
  }),
});

/** True if hotspot has valid normalized x/y (for validation). */
export const hasValidHotspotXY = (h) => {
  const x = Number(h?.x);
  const y = Number(h?.y);
  return Number.isFinite(x) && Number.isFinite(y);
};

/** Zone price must be explicitly set (empty default does not pass). */
export const hasValidZonePrice = (value) => {
  if (value === "" || value == null) return false;
  if (typeof value === "string" && !value.trim()) return false;
  const n = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(n) && n >= 0;
};

/**
 * Validate layout editor form. Returns all field errors plus the first error
 * for scrolling / messaging.
 */
export const validateLayoutForm = (form, t) => {
  const errors = [];
  const add = (message, fieldId, extra = {}) => {
    errors.push({ message, fieldId, ...extra });
  };

  if (!form.viewkey?.trim()) {
    add(t("TABLE_BOOKING_ERR_VIEWKEY"), "viewkey", { panel: "settings" });
  } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.viewkey.trim())) {
    add(t("TABLE_BOOKING_ERR_VIEWKEY_FORMAT"), "viewkey", { panel: "settings" });
  }

  if (!form.title?.trim()) {
    add(t("TABLE_BOOKING_ERR_TITLE"), "title", { panel: "settings" });
  }

  if (!form.sceneimageurl?.trim()) {
    add(t("TABLE_BOOKING_ERR_SCENE_IMAGE"), "sceneimageurl", {
      panel: "layout-image",
    });
  }

  for (let i = 0; i < (form.slots || []).length; i += 1) {
    const s = form.slots[i];
    if (!isValidSlotTime(s.startTime)) {
      add(t("TABLE_BOOKING_ERR_SLOT_TIME", { row: i + 1 }), `slot-${i}-startTime`, {
        panel: "settings",
        slotIndex: i,
      });
    }
    if (!isValidSlotTime(s.endTime)) {
      add(t("TABLE_BOOKING_ERR_SLOT_TIME", { row: i + 1 }), `slot-${i}-endTime`, {
        panel: "settings",
        slotIndex: i,
      });
    }
  }

  for (let i = 0; i < (form.areas || []).length; i += 1) {
    const a = form.areas[i];
    if (!a.label?.trim()) {
      add(t("TABLE_BOOKING_ERR_AREA_LABEL", { row: i + 1 }), `area-${i}-label`, {
        panel: "inspector",
        areaIndex: i,
      });
    }
    if (!hasValidHotspotXY(a.hotspot)) {
      add(t("TABLE_BOOKING_ERR_ZONE_HOTSPOT", { row: i + 1 }), `area-${i}-hotspot`, {
        panel: "canvas",
        areaIndex: i,
      });
    }
    if (!hasValidZonePrice(a.priceFromPerPerson)) {
      add(t("TABLE_BOOKING_ERR_ZONE_PRICE", { row: i + 1 }), `area-${i}-priceFromPerPerson`, {
        panel: "inspector",
        areaIndex: i,
      });
    }
    if (
      a.totalTableSlots == null ||
      !Number.isFinite(Number(a.totalTableSlots)) ||
      Number(a.totalTableSlots) < 1
    ) {
      add(t("TABLE_BOOKING_ERR_ZONE_SLOTS", { row: i + 1 }), `area-${i}-totalTableSlots`, {
        panel: "inspector",
        areaIndex: i,
      });
    }
    if (a.minPartySize > a.maxPartySize) {
      add(t("TABLE_BOOKING_ERR_PARTY_SIZE", { row: i + 1 }), `area-${i}-partySize`, {
        panel: "inspector",
        areaIndex: i,
      });
    }

    const tabs = Array.isArray(a.tables) ? a.tables : [];
    for (let j = 0; j < tabs.length; j += 1) {
      const tb = tabs[j];
      if (!tb.label?.trim()) {
        add(
          t("TABLE_BOOKING_ERR_TABLE_LABEL", { zone: i + 1, row: j + 1 }),
          `area-${i}-table-${j}-label`,
          { panel: "inspector", areaIndex: i, tableIndex: j }
        );
      }
      if (!hasValidHotspotXY(tb.hotspot)) {
        add(
          t("TABLE_BOOKING_ERR_TABLE_HOTSPOT", { zone: i + 1, row: j + 1 }),
          `area-${i}-table-${j}-hotspot`,
          { panel: "canvas", areaIndex: i, tableIndex: j }
        );
      }
      const tmin =
        tb.minPartySize === "" || tb.minPartySize == null
          ? null
          : Number(tb.minPartySize);
      const tmax =
        tb.maxPartySize === "" || tb.maxPartySize == null
          ? null
          : Number(tb.maxPartySize);
      if (
        tmin != null &&
        tmax != null &&
        Number.isFinite(tmin) &&
        Number.isFinite(tmax) &&
        tmin > tmax
      ) {
        add(
          t("TABLE_BOOKING_ERR_TABLE_PARTY_SIZE", { zone: i + 1, row: j + 1 }),
          `area-${i}-table-${j}-partySize`,
          { panel: "inspector", areaIndex: i, tableIndex: j }
        );
      }
    }
  }

  if (!errors.length) return { ok: true };
  return { ok: false, errors, first: errors[0] };
};

export const fieldErrorsToMap = (errors) =>
  (errors || []).reduce((acc, err) => {
    acc[err.fieldId] = err.message;
    return acc;
  }, {});
